import { useState, useEffect, useCallback } from "react";
import { save } from "./stamps";

const GOALS_KEY = "ppt_goals_v1";

/**
 * What you're working toward, and what you've decided not to do.
 *
 * Two kinds, because collecting goals come in two kinds:
 *
 *   rule   Something you've decided. "Singles over slabs." "No sealed."
 *          There is no progress bar, because there is no finish line — the
 *          point is that it's written down where you'll see it before you buy.
 *
 *   set    Finish one list. Progress is the list's own got-count, so it fills
 *          itself in as you collect.
 *
 *   value  A number for the collection to reach.
 *
 * Not split by game. How you collect is one set of decisions — "singles over
 * slabs" is not a Pokemon opinion — and splitting them would have meant
 * switching games to read half your own rules.
 */
const newId = () => `g${Date.now()}${Math.random().toString(36).slice(2, 6)}`;

export default function useGoals() {
  const [goals, setAll] = useState([]);
  // State rather than a ref: a ref flips inside the load effect and the save
  // effect then runs in the same commit holding the initial empty array.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(GOALS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) setAll(parsed);
    } catch {
      /* start empty */
    }
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      save(GOALS_KEY, JSON.stringify(goals));
    } catch {
      /* a blocked localStorage shouldn't lose the goal you just typed */
    }
  }, [goals, ready]);

  const addGoal = useCallback((goal) => {
    const text = String(goal?.text || "").trim();
    if (!text) return null;
    const entry = {
      id: newId(),
      kind: goal.kind === "set" || goal.kind === "value" ? goal.kind : "rule",
      text,
      listId: goal.listId ?? null,
      target: Number(goal.target) > 0 ? Number(goal.target) : null,
      done: false,
    };
    setAll((prev) => [...prev, entry]);
    return entry.id;
  }, []);

  const removeGoal = useCallback((id) => {
    setAll((prev) => prev.filter((g) => g.id !== id));
  }, []);

  // A rule you're keeping to has no progress, so it can still be ticked off
  // by hand — "no sealed" is either something you're doing or it isn't.
  const toggleDone = useCallback((id) => {
    setAll((prev) => prev.map((g) => (g.id === id ? { ...g, done: !g.done } : g)));
  }, []);

  return { goals, addGoal, removeGoal, toggleDone };
}
